/** "Waiting on …" — the item's unmet prerequisites, named.
 *
 *  Three components printed the raw action code (`waits on CATALOG_SYNC`) in a
 *  tooltip nobody could click. The serializer now sends `title` alongside
 *  `id`/`action`, so the blocker can be named the way the operator knows it and,
 *  where the host can reach the blocking row, jumped to.
 */
import { Chip, Link, Tooltip, Typography } from '@mui/material';

import type { PreflightBlockedBy } from '@/api/preflight';

const names = (blockers: PreflightBlockedBy[]) =>
  blockers.map((b) => b.title || b.action).join(', ');

/** The compact row form: one chip, named, jumping to the blocker when it can. */
export function BlockedChip({
  blockers,
  onJump,
}: {
  blockers: PreflightBlockedBy[];
  onJump?: (id: number) => void;
}) {
  const first = blockers[0];
  if (!first) return null;
  const label = blockers.length === 1 ? `Waiting on ${first.title || first.action}` : `Waiting on ${blockers.length} items`;
  const jump = onJump && blockers.length === 1 ? () => onJump(first.id) : undefined;

  return (
    <Tooltip title={blockers.length > 1 ? names(blockers) : 'Runs once the prerequisite completes'}>
      <Chip
        size="small"
        variant="outlined"
        label={label}
        onClick={jump}
        sx={{ height: 18, maxWidth: 240, cursor: jump ? 'pointer' : 'default' }}
      />
    </Tooltip>
  );
}

/** The panel form: a sentence, with each blocker linked where the host can. */
export function BlockedNotice({
  blockers,
  onJump,
}: {
  blockers: PreflightBlockedBy[];
  onJump?: (id: number) => void;
}) {
  if (blockers.length === 0) return null;
  return (
    <Typography variant="body2" component="span">
      Waiting on{' '}
      {blockers.map((b, i) => (
        <span key={b.id}>
          {i > 0 && ', '}
          {onJump ? (
            <Link component="button" type="button" variant="body2" onClick={() => onJump(b.id)}>
              {b.title || b.action}
            </Link>
          ) : (
            <strong>{b.title || b.action}</strong>
          )}
        </span>
      ))}
      {' — runs once the prerequisite completes.'}
    </Typography>
  );
}
